/**
 * Smart pairing for the Update panel (#114): suggestions the person can accept or ignore. Pure, so every rule is
 * unit-tested. Only statuses that exist in the org's list are ever suggested (names are matched case-insensitively).
 */
import { addBusinessDays, toLocalInput } from '../next-move/due'
import type { NextMoveValue } from '../next-move/editor'

export interface PairingStatus {
  name: string
  status_type: string
}

export type OwnerChoice = { kind: 'us'; userId?: string } | { kind: 'customer' } | { kind: 'contractor'; contractorId?: string }

export interface PairingInput {
  statuses: PairingStatus[]
  currentStatus: string | null | undefined
  /** The status picked in the panel ('' or the current one = unchanged). */
  pickedStatus: string
  timeMinutes: number
  bookingAt: string | null | undefined
  /** #343: derived server-side from a sent unscheduled WO or a typed remote/on-site booking. */
  fieldStatusSuggestion: 'Scheduling Required' | 'Scheduled Remote' | 'Scheduled On Site' | null | undefined
  /** #378/#390: the server's one shared reply-intent classification. */
  replyIntent?: 'close' | 'ask' | 'update' | 'resolve'
  replyAsked?: boolean
  now?: Date
}

export type Suggestion =
  | { id: string; kind: 'next_move'; label: string; text: string; owner: OwnerChoice; dueAt: string }
  | { id: string; kind: 'status'; label: string; status: string }

const eq = (a: string | null | undefined, b: string) => (a ?? '').trim().toLowerCase() === b.toLowerCase()
export const WCR = 'Waiting Client Response'
export const isWcr = (s: string | null | undefined) => eq(s, WCR)

function exists(statuses: PairingStatus[], name: string): string | null {
  return statuses.find((s) => eq(s.name, name))?.name ?? null
}

/**
 * #513: Approved and Rejected are another department's blueprint statuses ("a different dept that we dont use"), and
 * Closed - Moved to Opp is the CRM hand-off. None of them is ever the default close.
 */
const NOT_A_DEFAULT_CLOSE = ['approved', 'rejected', 'closed - moved to opp']

/** The status a plain close lands on: "Closed" when the org has it, else the first Closed-type plain close. */
export function closeTarget(statuses: PairingStatus[], preferred?: string | null): string | null {
  const closed = statuses.filter((status) => eq(status.status_type, 'Closed'))
  const want = preferred?.trim() || 'Closed'
  const hit = closed.find((status) => eq(status.name, want) && !NOT_A_DEFAULT_CLOSE.includes(status.name.trim().toLowerCase()))
  return hit?.name ?? closed.find((status) => !NOT_A_DEFAULT_CLOSE.includes(status.name.trim().toLowerCase()))?.name ?? null
}

export function suggestions(input: PairingInput): Suggestion[] {
  const now = input.now ?? new Date()
  const picked = input.pickedStatus.trim()
  const changed = picked !== '' && !eq(input.currentStatus, picked)
  const out: Suggestion[] = []

  if (!changed && (!picked || eq(input.currentStatus, picked))) {
    if (input.replyIntent === 'close') {
      const closed = closeTarget(input.statuses)
      if (closed && !eq(input.currentStatus, closed)) {
        out.push({ id: 'intent-close', kind: 'status', label: 'Close case', status: closed })
      }
    } else if (input.replyIntent === 'resolve') {
      const resolved = exists(input.statuses, 'Resolved – awaiting confirmation')
      if (resolved && !eq(input.currentStatus, resolved)) {
        out.push({ id: 'intent-resolved', kind: 'status', label: `Set status to ${resolved}`, status: resolved })
      }
    } else if (input.replyIntent === 'ask') {
      const waiting = exists(input.statuses, WCR)
      if (waiting && !eq(input.currentStatus, waiting)) {
        out.push({ id: 'intent-wcr', kind: 'status', label: `Set status to ${waiting}`, status: waiting })
      }
      if (input.replyAsked) {
        out.push({
          id: 'intent-waiting-customer',
          kind: 'next_move',
          label: 'Next move: Customer to reply, due in 2 business days',
          text: 'Customer to reply',
          owner: { kind: 'customer' },
          dueAt: addBusinessDays(now, 2).toISOString(),
        })
      }
    }
  }

  if (input.fieldStatusSuggestion) {
    const fieldStatus = exists(input.statuses, input.fieldStatusSuggestion)
    if (fieldStatus && !eq(picked || input.currentStatus, fieldStatus)) {
      out.push({
        id: 'field-service-status',
        kind: 'status',
        label: `Set status to ${fieldStatus}`,
        status: fieldStatus,
      })
    }
  }
  if (changed && isWcr(picked)) {
    out.push({
      id: 'wcr-next-move',
      kind: 'next_move',
      label: 'Next move: Customer to reply, due in 2 business days',
      text: 'Customer to reply',
      owner: { kind: 'customer' },
      dueAt: addBusinessDays(now, 2).toISOString(),
    })
  }
  if (changed && picked.toLowerCase().startsWith('scheduled') && input.bookingAt) {
    out.push({
      id: 'scheduled-next-move',
      kind: 'next_move',
      label: 'Next move at the booked visit',
      text: 'Visit as booked',
      owner: { kind: 'us' },
      dueAt: new Date(input.bookingAt).toISOString(),
    })
  }
  if (input.timeMinutes > 0 && eq(input.currentStatus, 'Open') && !changed) {
    const investigating = exists(input.statuses, 'Investigating')
    if (investigating)
      out.push({ id: 'time-investigating', kind: 'status', label: `Set status to ${investigating}`, status: investigating })
  }
  return out
}

/** WCR is a Desk workflow alert: the customer gets an email every time it is set (round 7). */
export function wcrRepeat(currentStatus: string | null | undefined, pickedStatus: string): boolean {
  return isWcr(currentStatus) && isWcr(pickedStatus)
}

/** Server prefill for a waiting Desk status (#1201). The suggestion chips stay; this is what Save writes. */
export interface WaitingMoveHint {
  status: string
  text: string
  owner_kind: 'customer' | 'contractor' | 'vendor'
  owner_contractor_id?: string | null
  due_at: string
}

export function hintForStatus(hints: WaitingMoveHint[] | null | undefined, status: string): WaitingMoveHint | null {
  const want = status.trim().toLowerCase()
  if (!want) return null
  return hints?.find((hint) => hint.status.trim().toLowerCase() === want) ?? null
}

/** A contractor hint with no firm id cannot be sent by the client; the server writes that move. */
export function waitingMoveValue(hint: WaitingMoveHint): NextMoveValue | null {
  const due = toLocalInput(new Date(hint.due_at))
  if (hint.owner_kind === 'customer') return { text: hint.text, owner: 'customer', due }
  if (hint.owner_kind === 'vendor') return { text: hint.text, owner: 'vendor', due }
  if (hint.owner_kind === 'contractor' && hint.owner_contractor_id) {
    return { text: hint.text, owner: `contractor:${hint.owner_contractor_id}`, due }
  }
  return null
}
